import React from 'react';
import { motion } from 'framer-motion';

// Founder tile depth + hover vocabulary — same constants used by the
// "Recent installs" gallery (RecentInstallsSection.jsx) so this section's
// photo cluster reads as the same card language. Rest: a barely-there cyan
// ring (12% alpha) + soft slate elevation; hover: cyan ring intensifies to
// 35%, 4px lift, cyan glow emerges, inner img scales 1.04 within the
// rounded frame.
const TILE_REST_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.12),' +
  ' 0 1px 2px rgba(15, 23, 42, 0.05),' +
  ' 0 10px 28px rgba(15, 23, 42, 0.07)';

const TILE_HOVER_SHADOW =
  'inset 0 0 0 1px rgba(7, 176, 233, 0.35),' +
  ' 0 2px 6px rgba(15, 23, 42, 0.07),' +
  ' 0 4px 12px rgba(15, 23, 42, 0.10),' +
  ' 0 20px 50px rgba(7, 176, 233, 0.18)';

const TILE_EASE = [0.16, 1, 0.3, 1];
const ENTRANCE_EASE = [0.25, 0.4, 0.25, 1]; // matches fadeInUp ease — already used elsewhere in this section

// Pass 3 entrance choreography:
//   • Left column has its own observer + staggerContainer. Eyebrow →
//     headline → body → quote, 80ms apart in reading rhythm.
//   • Right column (the photo grid) has its OWN observer (`amount: 0.15`)
//     so on mobile — where left stacks above right and the photos are
//     below the fold when the section's top first enters view — the tile
//     cascade fires when the user scrolls TO the photos, not when the
//     section's top crosses the viewport. On desktop both columns are at
//     the same scroll height so both observers fire near-simultaneously
//     and the entrance feels like one orchestrated reveal.
//   • Photo tile cascade lives on the GRID parent via delayChildren +
//     staggerChildren, NOT inside each tile's `visible` variant. That's
//     the trick: putting the delay on the parent means hovering off a
//     tile (whileHover → visible) doesn't re-fire the entrance delay.
//   • Fires once via `viewport={{ once: true }}` on each observer.

const leftColumnVariants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const photoGridVariants = {
  hidden: {},
  visible: { transition: { delayChildren: 0.10, staggerChildren: 0.10 } },
};

// Per-tile variants combine the Pass 2 hover treatment with the Pass 3
// entrance. The boxShadow is set to TILE_REST_SHADOW in the `hidden` state
// so it doesn't flicker in — only opacity + y animate during entrance.
const tileVariants = {
  hidden: { opacity: 0, y: 24, boxShadow: TILE_REST_SHADOW },
  visible: {
    opacity: 1,
    y: 0,
    boxShadow: TILE_REST_SHADOW,
    transition: { duration: 0.7, ease: ENTRANCE_EASE },
  },
  hover: {
    y: -4,
    boxShadow: TILE_HOVER_SHADOW,
    transition: { duration: 0.35, ease: TILE_EASE },
  },
};

// Inner img: scales only on hover. hidden/visible are no-ops to keep
// variant propagation clean (matches the parent's state names).
const mediaVariants = {
  hidden: { scale: 1 },
  visible: { scale: 1 },
  hover: { scale: 1.04, transition: { duration: 0.45, ease: TILE_EASE } },
};

// Per-element fade-up used by the left column's eyebrow / headline /
// body / quote. Curve unchanged from the prior staggerContainer-based
// version of this section so the motion vocabulary stays consistent.
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.7,
      ease: ENTRANCE_EASE,
    },
  },
};

const WhyWeBuiltSection = () => {
  return (
    <section className="relative bg-white py-12 lg:py-20 pb-16 sm:pb-16 overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-96 h-96 bg-cyan-100/20 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-blue-100/20 rounded-full blur-3xl" />
      </div>
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-slate-300/50 to-transparent" />
      <div className="max-w-7xl mx-auto px-5 lg:px-8 relative z-10">
        {/* Outer wrapper is intentionally NOT a motion element — each column
            below carries its OWN initial/whileInView observer so the photo
            cascade fires when the user scrolls to the photos on mobile
            (where the grid lives below the copy), not when the section's
            top first crosses the viewport. */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-20 items-start">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={leftColumnVariants}
            className="space-y-6"
          >
            <motion.div 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase"
              style={{ color: '#07b0e9' }}
            >
              <div className="w-8 h-px" style={{ background: 'linear-gradient(to right, #07b0e9, transparent)' }} />
              Who We Are
            </motion.div>

            <motion.h2 
              variants={fadeInUp}
              className="text-3xl lg:text-5xl xl:text-6xl font-extrabold text-slate-900 leading-[1.1] tracking-tight"
            >
              We built OpenbooQ because we{' '}
              <span className="bg-clip-text text-transparent" style={{ backgroundImage: 'linear-gradient(to right, #07b0e9, #0ea5e9)' }}>
                lived the problem.
              </span>
            </motion.h2>

            {/* Body copy — tuned for read-rate in Pass 2:
                  space-y-5 / lg:space-y-6  more breath between paragraphs (vs space-y-4)
                  leading-[1.75]            more vertical space within paragraphs
                  max-w-[600px]             caps line length at ~70-75 chars for comfortable reading
                The pivot line gets a 3px cyan left border + bigger font weight
                so a skimmer's eye lands on "we started to see how broken it
                really was" as a real turn in the story, not just another
                gray paragraph. Cyan bold anchors throughout untouched —
                they're doing scanner-eye work. */}
            <motion.div
              variants={fadeInUp}
              className="space-y-5 lg:space-y-6 text-slate-600 text-base lg:text-lg leading-[1.75] max-w-[600px]"
            >
              <p className="text-slate-700 font-medium">
                We're not outsiders trying to <span className="font-semibold" style={{ color: '#07b0e9' }}>"disrupt"</span> roofing. We owned and operated traditional roofing companies for years. We knocked doors. We sat at kitchen tables. We wrote estimates the way the industry taught us to.
              </p>
              <p
                className="text-xl lg:text-2xl font-semibold italic text-slate-900 pl-4 lg:pl-5 border-l-[3px] leading-snug"
                style={{ borderColor: '#07b0e9' }}
              >
                And over time, we started to see how <span style={{ color: '#07b0e9' }}>broken</span> it really was.
              </p>
              <p>
                Pricing varied wildly for the same roof. Homeowners paid for layers of sales commissions, inflated overhead, and unnecessary pressure tactics—without ever seeing where their money actually went. The process wasn't <span className="font-semibold" style={{ color: '#07b0e9' }}>transparent</span>, and it wasn't <span className="font-semibold" style={{ color: '#07b0e9' }}>fair</span>.
              </p>
              <p>
                Even when contractors had good intentions, the system itself pushed bad behavior.
              </p>
              <p className="text-slate-700 font-medium">
                OpenbooQ was built to fix that. We stripped roofing down to what actually matters: <span className="font-semibold" style={{ color: '#07b0e9' }}>material cost, standardized installation, and quality workmanship</span>. <span className="font-semibold" style={{ color: '#07b0e9' }}>No markups hidden in fine print. No sales games</span>. Just <span className="font-semibold" style={{ color: '#07b0e9' }}>clear pricing and a better experience</span> for homeowners.
              </p>
            </motion.div>

            <motion.div 
              variants={fadeInUp}
              className="pt-4 border-t border-slate-200"
            >
              <p className="text-sm text-slate-600 italic flex items-start gap-3">
                <span className="text-xl flex-shrink-0" style={{ color: '#07b0e9' }}>"</span>
                <span className="pt-1">We didn't imagine a better way to roof—we built it after years working inside the industry.</span>
                <span className="text-xl flex-shrink-0 self-end" style={{ color: '#07b0e9' }}>"</span>
              </p>
            </motion.div>
          </motion.div>

          {/* Right column — 2×3 founders photo grid (2 columns × 3 rows,
              6 tiles total). Expanded from 2×2 to 2×3 on 2026-05-26 to
              add two new portrait phone photos (IMG_1447 + IMG_1442).
              Tile order (left-to-right, top-to-bottom):
                row 1 left:   founders-rooftop  (5 founders at rooftop restaurant)
                row 1 right:  IMG_1447          (two girls smiling selfie — NEW)
                row 2 left:   founders-bar      (3 founders at a bar)
                row 2 right:  founders-duo      (2 founders at the bar in a restaurant)
                row 3 left:   IMG_1442          (girl holding other girl — NEW)
                row 3 right:  founders-studio   (4 founders in studio backdrop)
              Positions 4 ↔ 5 swapped 2026-05-26 (second pass).

              objectPosition is set to 'center 25%' on the two NEW portrait
              photos so the heads/faces (typically in the upper half of a
              vertical phone photo) sit safely inside the 4:3 tile crop —
              their source aspect is ~0.75:1 (tall) so default 'center'
              would face-crop. founders-rooftop and founders-studio keep
              the existing 'center 35%' bias from the prior 2×2 grid; the
              other two keep 'center'. Tile aspect-[4/3], rounded-2xl,
              and the cyan-ring rest+hover shadows are unchanged so all
              6 tiles read as one card language.

              The grid wrapper carries its own viewport observer — fires
              when 15% of the grid is visible. staggerChildren +
              delayChildren on the grid drive the cascade for all 6
              tiles. */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.15 }}
            variants={photoGridVariants}
            className="grid grid-cols-2 gap-3 lg:gap-4 lg:sticky lg:top-32"
          >
            {[
              { src: '/images/founders/founders-rooftop.webp', alt: 'OpenbooQ founders at a rooftop restaurant', objectPosition: 'center 35%' },
              { src: '/images/founders/IMG_1447.webp',         alt: 'Two OpenbooQ team members smiling in a selfie', objectPosition: 'center 25%' },
              { src: '/images/founders/founders-bar.webp',     alt: 'OpenbooQ founders at a bar', objectPosition: 'center' },
              { src: '/images/founders/founders-duo.webp',     alt: 'Two OpenbooQ founders in a restaurant', objectPosition: 'center' },
              { src: '/images/founders/IMG_1442.webp',         alt: 'Two OpenbooQ team members embracing', objectPosition: 'center 25%' },
              { src: '/images/founders/founders-studio.webp',  alt: 'OpenbooQ founders in a studio backdrop', objectPosition: 'center 35%' },
            ].map((photo) => (
              <motion.div
                key={photo.src}
                variants={tileVariants}
                whileHover="hover"
                className="relative rounded-2xl overflow-hidden bg-gray-100 aspect-[4/3]"
              >
                <motion.img
                  variants={mediaVariants}
                  src={photo.src}
                  alt={photo.alt}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ objectPosition: photo.objectPosition }}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default WhyWeBuiltSection;